import { CompactPagination } from "@/components/Pagination";
import { ActionTitle } from "@/components/actionTitle/ActionTitle";
import { getAppointments } from "@/features/calendar/utils";
import {
  ScrollContainer,
  WidgetContainer,
} from "@/features/workspace/widgets/Components";
import {
  type WidgetSetting,
  type WidgetSize,
  useCalendarAppointmentsSearch,
} from "@repo/api";
import { Text } from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import { localizeDateRange } from "@repo/ui";
import { useMemo } from "react";

import { CARD_HEIGHT_NORMAL } from "../constants";
import { AppointmentsTable } from "./AppointmentsTable";
import { getAppointmentWidgetSettings } from "./utils";

interface AppointmentsWidgetProps {
  title: string;
  settings?: WidgetSetting;
  widgetSize: WidgetSize;
}

export const AppointmentsWidget = ({
  title,
  settings,
  widgetSize,
}: AppointmentsWidgetProps) => {
  const { dateFnsLocale } = useTranslation();

  const widgetSettings = useMemo(
    () => getAppointmentWidgetSettings(settings),
    [settings]
  );

  const { data, isValidating, isLoading, size, setSize } =
    useCalendarAppointmentsSearch(widgetSettings, false);

  const appointments = useMemo(() => {
    let result = getAppointments(data);

    // Only apply FE filtering when BE does not handle it
    if (widgetSettings.status && widgetSettings.status.length > 0) {
      result = result.filter((appt) =>
        widgetSettings.status!.includes(appt.appointmentStatus)
      );
    }

    return result;
  }, [data, widgetSettings.status]);

  if (!isValidating && appointments.length === 0) {
    return (
      <WidgetContainer size={CARD_HEIGHT_NORMAL}>
        <ActionTitle title={`${title} (0)`} />
        <Text>
          {localizeDateRange(
            widgetSettings?.appointmentDate?.[0],
            widgetSettings?.appointmentDate?.[1],
            dateFnsLocale
          )}
        </Text>
        <Text>{t("prompt.no_search_result")}</Text>
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer size={CARD_HEIGHT_NORMAL}>
      <ActionTitle title={`${title} (${appointments.length})`} />
      <Text>
        {localizeDateRange(
          widgetSettings?.appointmentDate?.[0],
          widgetSettings?.appointmentDate?.[1],
          dateFnsLocale
        )}
      </Text>

      <ScrollContainer>
        <AppointmentsTable
          appointments={appointments}
          size={widgetSize}
          isLoading={isLoading}
          isValidating={isValidating}
        />
      </ScrollContainer>

      <CompactPagination
        setSize={setSize}
        size={size}
        totalNumber={appointments.length}
        currentNumber={appointments.length}
      />
    </WidgetContainer>
  );
};
